import React, { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { 
  AlertTriangle, 
  Info, 
  AlertCircle, 
  TrendingUp, 
  Calendar, 
  CreditCard,
  CheckCircle,
  XCircle
} from 'lucide-react'
import api from '../utils/api'
import Card from './ui/Card'
import Button from './ui/Button'

const QuotaStatus = ({ showDetails = false, className = "" }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [showUpgradeOptions, setShowUpgradeOptions] = useState(false)

  // Fetch quota status
  const { data: quotaStatus, isLoading, error } = useQuery({
    queryKey: ['quota-status'],
    queryFn: async () => {
      const response = await api.get('/user/quota/status')
      return response.data
    },
    refetchInterval: 30000, // Refetch every 30 seconds for real-time updates
  })

  // Fetch upgrade options when needed
  const { data: upgradeOptions } = useQuery({
    queryKey: ['upgrade-options'],
    queryFn: async () => {
      const response = await api.get('/user/quota/upgrade-options')
      return response.data
    },
    enabled: showUpgradeOptions
  })

  if (isLoading) {
    return (
      <div className={`animate-pulse bg-gray-200 rounded-lg h-20 ${className}`} />
    )
  }

  if (error || !quotaStatus) {
    return (
      <div className={`text-red-600 text-sm ${className}`}>
        Error loading quota status
      </div>
    )
  }

  const getStatusColor = (warningLevel) => {
    switch (warningLevel) {
      case 'critical': return 'text-red-600 bg-red-50 border-red-200'
      case 'warning': return 'text-orange-600 bg-orange-50 border-orange-200'
      case 'notice': return 'text-yellow-600 bg-yellow-50 border-yellow-200'
      default: return 'text-green-600 bg-green-50 border-green-200'
    }
  }

  const getStatusIcon = (warningLevel) => {
    switch (warningLevel) {
      case 'critical': return <XCircle className="w-5 h-5" />
      case 'warning': return <AlertTriangle className="w-5 h-5" />
      case 'notice': return <AlertCircle className="w-5 h-5" />
      default: return <CheckCircle className="w-5 h-5" />
    }
  }

  const getStatusMessage = () => {
    if (quotaStatus.is_exceeded) {
      return {
        title: t('quota.exceeded.title'),
        message: t('quota.exceeded.message', {
          overage: quotaStatus.overage_amount.toLocaleString(),
          days: quotaStatus.days_until_renewal
        })
      }
    }
    
    if (quotaStatus.warning_level === 'warning') {
      return {
        title: t('quota.warning.title'),
        message: t('quota.warning.message', {
          percentage: quotaStatus.percentage_used,
          remaining: quotaStatus.remaining.toLocaleString()
        })
      }
    }
    
    if (quotaStatus.warning_level === 'notice') {
      return {
        title: t('quota.notice.title'),
        message: t('quota.notice.message', {
          percentage: quotaStatus.percentage_used
        })
      }
    }
    
    return {
      title: t('quota.normal.title'),
      message: t('quota.normal.message', {
        used: quotaStatus.current_usage.toLocaleString(),
        limit: quotaStatus.monthly_limit.toLocaleString()
      })
    }
  }

  const status = getStatusMessage()
  const colorClasses = getStatusColor(quotaStatus.warning_level)

  if (!showDetails) {
    // Compact view for dashboard/header
    return (
      <div className={`${className} ${colorClasses} border rounded-lg p-3`}>
        <div className="flex items-center space-x-2">
          {getStatusIcon(quotaStatus.warning_level)}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{status.title}</div>
            <div className="text-xs opacity-75">
              {quotaStatus.current_usage.toLocaleString()} / {quotaStatus.monthly_limit.toLocaleString()} {t('translation.characters').toLowerCase()}
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm font-bold">{quotaStatus.percentage_used}%</div>
            {quotaStatus.is_exceeded && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate('/payment')}
                className="mt-1"
              >
                {t('quota.upgrade')}
              </Button>
            )}
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-300 ${
              quotaStatus.warning_level === 'critical' ? 'bg-red-500' :
              quotaStatus.warning_level === 'warning' ? 'bg-orange-500' :
              quotaStatus.warning_level === 'notice' ? 'bg-yellow-500' : 'bg-green-500'
            }`}
            style={{ width: `${Math.min(quotaStatus.percentage_used, 100)}%` }}
          />
        </div>
      </div>
    )
  }

  // Detailed view for settings/quota page
  return (
    <div className={className}>
      <Card>
        <Card.Header>
          <Card.Title className="flex items-center space-x-2">
            <TrendingUp className="w-5 h-5" />
            <span>{t('quota.title')}</span>
          </Card.Title>
        </Card.Header>
        
        <Card.Content className="space-y-6">
          {/* Status Alert */}
          <div className={`${colorClasses} border rounded-lg p-4`}>
            <div className="flex items-start space-x-3">
              {getStatusIcon(quotaStatus.warning_level)}
              <div className="flex-1">
                <h4 className="font-medium">{status.title}</h4>
                <p className="text-sm mt-1">{status.message}</p>
                {quotaStatus.is_exceeded && (
                  <div className="mt-3 space-x-2">
                    <Button
                      size="sm"
                      onClick={() => navigate('/payment')}
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      {t('quota.upgrade')}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate('/payment')}
                    >
                      {t('quota.viewUsage')}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Usage Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-gray-900">
                {quotaStatus.current_usage.toLocaleString()}
              </div>
              <div className="text-sm text-gray-600">{t('quota.currentUsage')}</div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-gray-900">
                {quotaStatus.remaining.toLocaleString()}
              </div>
              <div className="text-sm text-gray-600">{t('quota.remaining')}</div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-gray-900">
                {quotaStatus.percentage_used}%
              </div>
              <div className="text-sm text-gray-600">{t('quota.percentageUsed')}</div>
            </div>
          </div>

          {/* Usage Progress */}
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span>{t('quota.monthlyUsage')}</span>
              <span>{quotaStatus.current_usage.toLocaleString()} / {quotaStatus.monthly_limit.toLocaleString()}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className={`h-3 rounded-full transition-all duration-500 ${
                  quotaStatus.warning_level === 'critical' ? 'bg-red-500' :
                  quotaStatus.warning_level === 'warning' ? 'bg-orange-500' :
                  quotaStatus.warning_level === 'notice' ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{ width: `${Math.min(quotaStatus.percentage_used, 100)}%` }}
              />
            </div>
            {quotaStatus.is_exceeded && (
              <div className="text-xs text-red-600 mt-1">
                {t('quota.exceededBy', { amount: quotaStatus.overage_amount.toLocaleString() })}
              </div>
            )}
          </div>

          {/* Renewal Information */}
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Calendar className="w-4 h-4" />
            <span>
              {t('quota.renewsIn', { days: quotaStatus.days_until_renewal })} 
              ({new Date(quotaStatus.period_end).toLocaleDateString()})
            </span>
          </div>
        </Card.Content>
      </Card>

      {/* Upgrade Options Modal */}
      {showUpgradeOptions && upgradeOptions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-screen overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">{t('quota.upgradeOptions')}</h3>
              <button
                onClick={() => setShowUpgradeOptions(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            
            <div className="mb-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <Info className="w-4 h-4 inline mr-2" />
                {t('quota.carryoverInfo')}
              </p>
            </div>

            <div className="grid gap-4">
              {upgradeOptions.upgrade_options.map((option, index) => (
                <div
                  key={option.plan}
                  className={`border rounded-lg p-4 ${
                    option.recommended ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                  }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h4 className="font-medium">{option.name}</h4>
                      <div className="text-2xl font-bold text-blue-600">{option.price}</div>
                    </div>
                    {option.recommended && (
                      <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded">
                        {t('quota.recommended')}
                      </span>
                    )}
                  </div>
                  
                  <div className="text-sm text-gray-600 mb-3">
                    <strong>{option.monthly_limit.toLocaleString()}</strong> characters/month
                  </div>
                  
                  <div className="text-sm text-green-600 mb-3">
                    {option.savings}
                  </div>
                  
                  <ul className="text-sm text-gray-600 mb-4">
                    {option.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center">
                        <CheckCircle className="w-3 h-3 text-green-500 mr-2" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <Button
                    className="w-full"
                    variant={option.recommended ? "primary" : "outline"}
                    onClick={() => {
                      setShowUpgradeOptions(false)
                      navigate('/payment')
                    }}
                  >
                    {t('quota.upgradeTo', { plan: option.name })}
                  </Button>
                </div>
              ))}
            </div>

            <div className="mt-6 text-center">
              <Button variant="ghost" onClick={() => setShowUpgradeOptions(false)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default QuotaStatus